<template>
    <!--
      Shared sub-navigation for the two graph curation surfaces. The sidebar only
      renders two levels, so the grouping lives here as a tab strip that both
      pages render directly under their header.
    -->
    <div v-if="visibleTabs.length > 1" class="curation-tabs">
        <router-link
            v-for="tab in visibleTabs"
            :key="tab.name"
            :to="{ name: tab.name, params: { cid } }"
            class="curation-tab"
            :class="{ active: $route.name === tab.name }"
        >
            <i :class="tab.icon"></i>
            <span>{{ tab.label }}</span>
            <small v-if="tab.hint" class="curation-tab-hint">{{ tab.hint }}</small>
        </router-link>
    </div>
</template>

<script>
import { getCurationAvailability } from '../utils/curationAvailability'
import { getAgmClientOverrides } from '../utils/agmClientOverrides'

export default {
    name: 'CurationTabs',
    data() {
        return {
            tabs: [
                {
                    label: 'Recommendations',
                    hint: 'merge / split',
                    icon: 'fa fa-code-branch',
                    name: 'Graph Curation',
                },
                {
                    label: 'Cluster Formation',
                    hint: 'how a cluster formed',
                    icon: 'fa fa-diagram-project',
                    name: 'Cluster Formation',
                },
            ],
            /* null until probed: every tab shows meanwhile, so nothing flickers out and back. */
            availability: null,
        }
    },
    computed: {
        /* The selected client wins over the URL: if the two ever disagree, a tab
           must not carry the stale cid along and switch the client back. */
        cid() {
            return this.$store.state.currentClient?.cid || this.$route.params.cid || ''
        },
        /**
         * Recommendations shows when merge or split has a configured table (a 404 from the config
         * API means none); Cluster Formation is hidden for clients whose overrides say no such data is produced.
         */
        visibleTabs() {
            const a = this.availability
            const hideFormation = !!getAgmClientOverrides(this.cid)?.hideClusterFormation
            return this.tabs.filter((t) => {
                if (t.name === 'Graph Curation') return !a || a.merge || a.split
                if (t.name === 'Cluster Formation') return !hideFormation
                return true
            })
        },
    },
    watch: {
        cid: {
            immediate: true,
            async handler(cid) {
                this.availability = null
                const a = await getCurationAvailability(this.$store, cid)
                if (cid !== this.cid) return
                this.availability = a
                this.leaveHiddenTab()
            },
        },
    },
    methods: {
        /* Landing on a hidden tab (a bookmark, a cid switch) moves to the first one that shows.
           With none left the page stays and explains there is nothing to curate. */
        leaveHiddenTab() {
            const visible = this.visibleTabs
            if (visible.some((t) => t.name === this.$route.name)) return
            /* Nothing visible: Graph Curation is the page that says so. */
            const target = visible.length ? visible[0].name : 'Graph Curation'
            if (target === this.$route.name) return
            this.$router.replace({ name: target, params: { cid: this.cid } }).catch(() => {})
        },
    },
}
</script>

<style scoped>
.curation-tabs {
    display: flex;
    gap: 4px;
    padding: 0 0 0 2px;
    border-bottom: 1px solid #e6eaf0;
    margin-bottom: 14px;
}
.curation-tab {
    display: inline-flex;
    align-items: baseline;
    gap: 7px;
    padding: 9px 16px;
    font-size: 13px;
    font-weight: 600;
    color: #7c8798;
    text-decoration: none;
    border-bottom: 2px solid transparent;
    margin-bottom: -1px;
    transition: color 0.14s, border-color 0.14s;
    white-space: nowrap;
}
.curation-tab i {
    font-size: 11px;
    opacity: 0.75;
}
.curation-tab:hover {
    color: #0f172a;
}
.curation-tab.active {
    color: #0f766e;
    border-bottom-color: #0f766e;
}
.curation-tab-hint {
    font-size: 10px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    opacity: 0.6;
}
@media (max-width: 575.98px) {
    .curation-tab-hint {
        display: none;
    }
}
</style>

<style lang="scss">
@use '../theme/scss/_user-variables.scss' as *;

body.dark-mode {
    .curation-tabs { border-bottom-color: $dark-border-color; }
    .curation-tab { color: $dark-text-muted; }
    .curation-tab:hover { color: $dark-text-primary; }
    .curation-tab.active { color: #2dd4bf; border-bottom-color: #2dd4bf; }
}
</style>
